
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DashboardMetrics {
  totalEpisodes: number;
  totalActivities: number;
  totalPartners: number;
  totalCharacters: number;
  recentEpisodes: number;
  recentActivities: number;
  recentPartners: number;
  recentCharacters: number;
}

export const useDashboardMetrics = () => {
  return useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: async (): Promise<DashboardMetrics> => {
      console.log('Fetching dashboard metrics...');
      
      // Get current date and 30 days ago
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      // Fetch episodes data
      const { data: episodes, error: episodesError } = await supabase
        .from('episodes')
        .select('created_at');
      
      if (episodesError) {
        console.error('Error fetching episodes:', episodesError);
      }
      
      // Fetch activities data
      const { data: activities, error: activitiesError } = await supabase
        .from('activities')
        .select('created_at');
      
      if (activitiesError) {
        console.error('Error fetching activities:', activitiesError);
      }
      
      // Fetch partners data
      const { data: partners, error: partnersError } = await supabase
        .from('partners')
        .select('created_at');
      
      if (partnersError) {
        console.error('Error fetching partners:', partnersError);
      }
      
      // Fetch characters data
      const { data: characters, error: charactersError } = await supabase
        .from('characters')
        .select('created_at');
      
      if (charactersError) {
        console.error('Error fetching characters:', charactersError);
      }
      
      // Calculate metrics
      const totalEpisodes = episodes?.length || 0;
      const totalActivities = activities?.length || 0;
      const totalPartners = partners?.length || 0;
      const totalCharacters = characters?.length || 0;
      
      // Calculate recent additions (last 30 days)
      const recentEpisodes = episodes?.filter(ep => 
        new Date(ep.created_at) >= thirtyDaysAgo
      ).length || 0;
      
      const recentActivities = activities?.filter(act => 
        new Date(act.created_at) >= thirtyDaysAgo
      ).length || 0;
      
      const recentPartners = partners?.filter(partner => 
        new Date(partner.created_at) >= thirtyDaysAgo
      ).length || 0;
      
      const recentCharacters = characters?.filter(character => 
        new Date(character.created_at) >= thirtyDaysAgo
      ).length || 0;
      
      console.log('Dashboard metrics calculated:', {
        totalEpisodes,
        totalActivities,
        totalPartners,
        totalCharacters,
        recentEpisodes,
        recentActivities,
        recentPartners,
        recentCharacters
      });
      
      return {
        totalEpisodes,
        totalActivities,
        totalPartners,
        totalCharacters,
        recentEpisodes,
        recentActivities,
        recentPartners,
        recentCharacters
      };
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};
